import type { LucideIcon } from "lucide-react";
import { useCallback, useEffect } from "react";
import { useSearchParams } from "wouter";
import { Button } from "./ui/Button";

interface TabItem<T extends string = string> {
  key: T;
  label?: string;
  icon?: LucideIcon;
  disabled?: boolean;
}

interface TabsProps<T extends string> {
  tabs: readonly TabItem<T>[];
  value: T;
  onChange: (tab: T) => void;
  className?: string;
  searchParamsKey?: string;
}

/**
 * Reusable Tabs component with full type safety
 *
 * @example
 * ```tsx
 * const tabs = [
 *   { key: 'devices', label: 'Dispositivos', icon: Cpu },
 *   { key: 'pumps', label: 'Bombas', icon: Wrench }
 * ] as const;
 *
 * <Tabs
 *   tabs={tabs}
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 * />
 * ```
 */
function Tabs<T extends string>({
  tabs,
  value: activeTab,
  onChange: onTabChange,
  className = "",
  searchParamsKey = "activeTab",
}: TabsProps<T>) {
  const [searchParams, setSearchParams] = useSearchParams();

  const changeTab = (tab: T) => {
    const tabItem = tabs.find((t) => t.key === tab) ?? tabs[0];
    if (!tabItem?.disabled) {
      onTabChange && onTabChange(tabItem.key);
    }
  };

  const handleTabClick = useCallback(
    (tab: T) => {
      changeTab(tab);
    },
    [tabs, onTabChange]
  );

  useEffect(() => {
    if (searchParamsKey) {
      activeTab = searchParams.get(searchParamsKey) as T;
      const oldOnTabChange = onTabChange;
      onTabChange = (tab: T) => {
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set(searchParamsKey, tab);
          return newParams;
        });
        oldOnTabChange(tab);
      };
    }
  }, [searchParamsKey, searchParams, setSearchParams]);

  useEffect(() => {
    changeTab(activeTab);
  }, [activeTab]);

  return (
    <div className={`flex bg-gray-100 rounded-lg p-1 ${className}`}>
      {tabs.map((tab) => {
        const isActive = activeTab === tab.key;
        const Icon = tab.icon;

        return (
          <Button
            key={tab.key}
            onClick={() => handleTabClick(tab.key)}
            disabled={tab.disabled}
            variant="neutral"
            ghost={!isActive}
            size="md"
            className={`flex-1 min-w-0 flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 py-1.5 sm:py-2 ${
              tab.disabled ? "text-gray-400 cursor-not-allowed" : ""
            }`}
            aria-pressed={isActive}
            aria-disabled={tab.disabled}
            aria-label={tab.label}
            icon={Icon && <Icon size={16} />}
          >
            {tab.label && (
              <span className="text-xs sm:text-sm leading-tight text-center break-words">
                {tab.label}
              </span>
            )}
          </Button>
        );
      })}
    </div>
  );
}

export default Tabs;
export type { TabItem, TabsProps };
